import { useEffect, useRef } from "react";
import { X, Download, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { iosSave } from "../lib/iosSave";

/**
 * VideoPlayerDialog — full-screen lightbox that plays an XEA video inside
 * the app using the native <video> element. Replaces the previous flow
 * where clicking "Play" opened the video in a new browser tab.
 *
 * Props
 *  - open: boolean
 *  - onClose: () => void
 *  - url: string             // the video URL to play
 *  - title?: string          // optional header label
 *  - downloadUrl?: string    // optional override; defaults to `url`. The
 *                             // dialog's Download button opens it in a
 *                             // NEW tab so the user can save it without
 *                             // losing the dialog.
 *
 * Keyboard:
 *  - Esc closes the dialog
 */
export default function VideoPlayerDialog({ open, onClose, url, title, downloadUrl }) {
  const videoRef = useRef(null);

  // Esc to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Pause + reset when closing so audio doesn't keep playing
  useEffect(() => {
    if (!open && videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (_e) { /* ignore */ }
    }
  }, [open]);

  const dl = downloadUrl || url;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 sm:p-8"
          onClick={onClose}
          data-testid="video-player-dialog"
        >
          <motion.div
            initial={{ scale: 0.94, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, y: 10 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative w-full max-w-5xl bg-ink-950 rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold">
                  XEA · player
                </p>
                <p className="text-sm font-semibold text-white truncate" data-testid="video-player-title">
                  {title || "Video preview"}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Download — iOS gets the native "Save to Files / Drive"
                    sheet via Web Share API; other platforms get a new
                    tab with download intent. Never closes the player. */}
                {dl && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                    onClick={() => iosSave(dl, { kind: "video", filename: `${(title || "video").replace(/[^\w-]+/g, "_").slice(0, 40)}.mp4` })}
                    data-testid="video-player-download"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                  </Button>
                )}
                {/* Open in standalone tab (escape hatch) */}
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in new tab"
                    data-testid="video-player-external"
                  >
                    <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 px-2">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </a>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition"
                  data-testid="video-player-close"
                  aria-label="Close player"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Video */}
            <video
              ref={videoRef}
              src={url}
              controls
              autoPlay
              playsInline
              className="w-full max-h-[80vh] bg-black"
              data-testid="video-player-element"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
